import { formatLandingReviewDate } from "@/lib/landingExample";
import { narrativeScenes } from "@/lib/landingNarrative";
import styles from "./LandingNarrative.module.css";

function HumanChoice({ content, choice, t, interactive, compact = false, onChoice, onCommit }) {
  const selected = content.choices[choice];
  return <article className={`${styles.human} ${compact ? styles.compactHuman : ""}`} data-testid="landing-decision">
    <div className={styles.heading}><h3>{t("layer.decision")}</h3></div>
    {interactive && <>
      <p className={styles.note}>{t("landing.narrative.choiceHint")}</p>
      <div className={styles.choices}>
        <button type="button" className={styles.control} aria-pressed={choice === "recommended"} onClick={() => onChoice("recommended")} data-testid="narrative-follow">{t("landing.narrative.follow")}</button>
        <button type="button" className={styles.control} aria-pressed={choice === "other"} onClick={() => onChoice("other")} data-testid="narrative-other">{t("landing.narrative.other")}</button>
      </div>
    </>}
    <p className={styles.option} data-testid="landing-example-choice">{selected.option.name}</p>
    <div className={styles.reason}><span className={styles.label}>{t("landing.narrative.humanReason")}</span><p data-testid="narrative-human-rationale">{selected.rationale}</p></div>
    <div className={styles.commitRow}>
      <span>{t("landing.visual.userBoundary")}</span>
      {interactive && <button type="button" className={styles.control} onClick={onCommit} data-testid="narrative-commit">{t("landing.narrative.commit")}</button>}
    </div>
  </article>;
}

// Also rendered at build time for the no-JavaScript fallback. Content has one source.
export function LandingNarrativeFrames({ content, horizon, t, lang, scene = 0, choice = "other", staticMode = false, animated = true, onChoice, onCommit }) {
  const scenes = staticMode ? narrativeScenes : [narrativeScenes[scene]];
  return <div className={staticMode ? styles.staticFlow : undefined} data-testid="narrative-frames">
    {scenes.map((step) => {
      const index = narrativeScenes.indexOf(step);
      return <div key={step.id} className={`${styles.frame} ${!staticMode && animated ? styles.animated : ""}`} data-testid={`narrative-frame-${index}`}>
        <div className={styles.heading}><h3>{String(index + 1).padStart(2, "0")} / {t(step.label)}</h3></div>
        {index === 0 && <div className={styles.questionStage}>
          {!staticMode && <div className={styles.artifacts} aria-hidden="true">
            {content.items.map((item, i) => <div key={item.id} className={styles.artifact}><span>{t(content.artifacts[i])}</span>{item.text}</div>)}
          </div>}
          <div className={styles.prompt}>
            <p data-testid="landing-example-question">{content.question}</p>
            <span className={styles.horizon} data-testid="landing-example-horizon">{t("field.horizon")} · {t("horizon.days", { n: horizon })}</span>
          </div>
          <p className={styles.note}>{t("landing.narrative.focus")}</p>
        </div>}
        {index === 1 && <article data-testid="landing-facts">
          <div className={styles.heading}><span>{t("brief.composing")}</span><span>{t("landing.narrative.confirmed")}</span></div>
          <ul className={styles.factGrid}>
            {content.items.map((item, i) => <li key={item.id} className={`${styles.fact} ${styles[`fact${item.epistemic}`] || ""}`} style={{ "--arrival-delay": `${i * 300}ms` }} data-testid={`landing-example-item-${item.id}`}>
              <div className={styles.factTop}><span>{item.id}</span><span>{t(`epistemic.${item.epistemic}`)}</span></div>
              <p>{item.text}</p>
            </li>)}
          </ul>
          <p className={styles.footnote}>{t("landing.story.factNote")}</p>
        </article>}
        {index === 2 && <article className={styles.ai} data-testid="landing-inference">
          <div className={styles.heading}><h3 className={styles.aiBadge}>{t("layer.inference")}</h3></div>
          <span className={styles.label}>{t("brief.recommendation")}</span>
          <p className={styles.option} data-testid="landing-example-recommendation">{content.recommendation.name}</p>
          <div className={styles.reason}><span className={styles.label}>{t("landing.narrative.aiReason")}</span><p>{content.because}</p></div>
          <div className={styles.meta}><span>{t("brief.confidence")}: <span data-testid="landing-example-confidence">{content.confidence}</span></span><span>{t("landing.visual.aiBoundary")}</span></div>
        </article>}
        {index === 3 && <HumanChoice {...{ content, choice, t, onChoice, onCommit }} interactive={!staticMode} />}
        {index === 4 && <>
          <p className={styles.note}>{t("landing.narrative.commitActor")}</p>
          <div className={staticMode ? undefined : styles.completion}>
          {!staticMode && <HumanChoice {...{ content, choice, t }} interactive={false} compact />}
          <article className={styles.record} data-testid="narrative-record">
            <div className={styles.seal}><span aria-hidden="true">✓</span><strong>{t("landing.narrative.recorded")}</strong></div>
            <p className={styles.note}>{t("landing.narrative.saved")}</p>
            <dl className={styles.followThrough} data-testid="landing-example-follow-through">
              <div><dt>{t("commit.nextAction")}</dt><dd data-testid="landing-example-next-action">{content.followThrough.nextAction}</dd></div>
              <div><dt>{t("commit.reviewDate")}</dt><dd><time dateTime={content.followThrough.reviewAt} data-testid="landing-example-review-date">{formatLandingReviewDate(content.followThrough.reviewAt, lang)}</time></dd></div>
            </dl>
          </article>
          </div>
        </>}
      </div>;
    })}
  </div>;
}
